import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, Wallet, Users } from 'lucide-react';

const Solution = () => {
  const solutions = [
    {
      icon: Rocket,
      title: 'Lightning Fast',
      points: [
        '2-4 weeks delivery (not 6 months)',
        'Weekly demos and iterations',
        'Modern tech stack (Claude, OpenClaw, Next.js)',
      ],
      color: 'text-[#00e5ff]',
      bgColor: 'bg-cyan-50',
    },
    {
      icon: Wallet,
      title: 'Radically Affordable',
      points: [
        '₹50k-20L (not ₹50L-2Cr)',
        'Fixed pricing, no surprises',
        '90% AI-powered development',
      ],
      color: 'text-[#1a237e]',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Users,
      title: 'Direct Access',
      points: [
        'Talk to founders directly',
        'WhatsApp/Telegram support',
        'Real-time progress updates',
      ],
      color: 'text-[#ff6d00]',
      bgColor: 'bg-orange-50',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <section className="section-padding bg-white" data-testid="solution-section">
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="text-sm font-medium tracking-wide uppercase text-[#00e5ff] mb-3 block">
            Our Solution
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#212121] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="solution-headline"
          >
            The LitheForce Difference
          </h2>
          <p className="text-base md:text-lg text-[#5a6b8c] max-w-2xl mx-auto">
            AI-native agency built for the modern era
          </p>
        </motion.div>

        {/* Solution Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-6 md:gap-8"
        >
          {solutions.map((solution, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm hover:shadow-lg hover:border-cyan-400/30 transition-all duration-300 card-hover"
              data-testid={`solution-card-${index}`}
            >
              <div className={`w-12 h-12 rounded-xl ${solution.bgColor} flex items-center justify-center mb-5`}>
                <solution.icon className={`w-6 h-6 ${solution.color}`} />
              </div>
              <h3
                className="text-xl md:text-2xl font-semibold text-[#212121] mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {solution.title}
              </h3>
              <ul className="space-y-3">
                {solution.points.map((point, pIndex) => (
                  <li key={pIndex} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#5a6b8c]">{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Solution;
